import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocaleProvider, useT } from './context/LocaleContext';
import BookingsPage from './pages/Bookings';
import BookingDetailPage from './pages/BookingDetail';
import CustomerDetailPage from './pages/CustomerDetail';
import CustomersPage from './pages/Customers';
import LoginPage from './pages/Login';
import {
  BusinessSetupPage,
  BrandsSetupPage,
  HoursSetupPage,
  LocationsPage,
  SetupHome,
  TeamPage,
} from './pages/Management';
import OverviewPage from './pages/Overview';
import AlertsPage from './pages/Alerts';
import FinancesPage from './pages/Finances';
import ServicesPage from './pages/Services';
import ReviewsPage from './pages/Reviews';
import ResaleInspectionsPage from './pages/ResaleInspections';
import ResaleInspectionDetailPage from './pages/ResaleInspectionDetail';
import ResaleInspectionBookingPage from './pages/ResaleInspectionBookingPage';
import PublicResaleReportPage from './pages/PublicResaleReport';
import PublicQuotePage from './pages/PublicQuote';
import TechnicianActivityPage from './pages/TechnicianActivity';
import { canAccessPath, firstAllowedPath } from './lib/deskNav';

function Gate({ children }) {
  const { loading, session, isStaff, profile } = useAuth();
  const location = useLocation();
  const t = useT();

  if (loading) {
    return <div className="splash">{t('login.loading')}</div>;
  }
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!isStaff) {
    return (
      <div className="splash">
        {t('login.notStaff')}
        {profile?.role ? ` (${profile.role})` : ''}.
      </div>
    );
  }
  if (!canAccessPath(profile, location.pathname)) {
    return <Navigate to={firstAllowedPath(profile)} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/r/:token" element={<PublicResaleReportPage />} />
      <Route path="/q/:token" element={<PublicQuotePage />} />
      <Route
        path="/"
        element={
          <Gate>
            <Layout />
          </Gate>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="bookings/:id" element={<BookingDetailPage />} />
        <Route path="hours" element={<Navigate to="/management/hours" replace />} />
        <Route path="admins" element={<Navigate to="/management/team" replace />} />
        <Route path="mechanics" element={<Navigate to="/management/team" replace />} />
        <Route path="management" element={<SetupHome />} />
        <Route path="management/locations" element={<LocationsPage />} />
        <Route path="management/hours" element={<HoursSetupPage />} />
        <Route path="management/brands" element={<BrandsSetupPage />} />
        <Route path="management/team" element={<TeamPage />} />
        <Route path="management/business" element={<BusinessSetupPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="finances" element={<FinancesPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="resale-inspections" element={<ResaleInspectionsPage />} />
        <Route
          path="resale-inspections/booking/:bookingId"
          element={<ResaleInspectionBookingPage />}
        />
        <Route path="resale-inspections/:id" element={<ResaleInspectionDetailPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="mechanic-activity" element={<TechnicianActivityPage />} />
        <Route path="technician-activity" element={<Navigate to="/mechanic-activity" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </LocaleProvider>
  );
}
